import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  healthApi,
  type HealthCheck,
  type HealthSeverity,
} from "@/api/health";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status, type StatusTone } from "@/components/ui/Status";

/**
 * System health dashboard (issue #23).
 *
 * Glanceable view of "is anything quietly broken?" — one card per
 * check from `/api/v1/health/dashboard`. The backend already runs the
 * checks concurrently so we only need a single GET.
 *
 * The page auto-refreshes every 30s. That's tight enough that the
 * indicator goes green within seconds of fixing the underlying
 * problem, but loose enough that the indexer/client liveness probes
 * the backend runs on a slower schedule still drive the result —
 * polling more aggressively wouldn't actually surface fresher data.
 */
const REFRESH_MS = 30_000;

export function Health() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["health", "dashboard"],
    queryFn: healthApi.dashboard,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const summary = useMemo(() => rollup(data), [data]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="System"
        title="Health"
        description={subhead(summary, isLoading, isError)}
        actions={
          <RollupChips summary={summary} fetching={isFetching && !isLoading} />
        }
      />

      {isLoading ? (
        <Skeleton.Rows count={6} />
      ) : isError ? (
        <EmptyState
          glyph="default"
          title="Couldn't load health checks"
          description="The dashboard endpoint failed. Check the server log and try again."
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="focus-ring rounded-md border border-line bg-surface-2/40 px-3 py-1.5 text-sm text-foreground/80 hover:border-line-strong hover:text-foreground"
            >
              Retry
            </button>
          }
        />
      ) : !data || data.length === 0 ? (
        <EmptyState
          glyph="default"
          title="No checks ran"
          description="The server returned an empty list — nothing to display."
        />
      ) : (
        <ul className="space-y-3">
          {data.map((check) => (
            <li key={check.id}>
              <CheckCard check={check} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface Summary {
  ok: number;
  warning: number;
  error: number;
  total: number;
}

function rollup(checks: HealthCheck[] | undefined): Summary {
  const out: Summary = { ok: 0, warning: 0, error: 0, total: 0 };
  if (!checks) return out;
  for (const c of checks) {
    out[c.severity] += 1;
    out.total += 1;
  }
  return out;
}

function subhead(s: Summary, loading: boolean, errored: boolean): string {
  if (loading) return "Running checks…";
  if (errored) return "Couldn't reach the dashboard endpoint.";
  if (s.error > 0) {
    return `${s.error} error${s.error === 1 ? "" : "s"} need attention. Auto-refreshes every 30 seconds.`;
  }
  if (s.warning > 0) {
    return `${s.warning} warning${s.warning === 1 ? "" : "s"} to look at. Auto-refreshes every 30 seconds.`;
  }
  return `All ${s.total} checks healthy. Auto-refreshes every 30 seconds.`;
}

function RollupChips({
  summary,
  fetching,
}: {
  summary: Summary;
  fetching: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {summary.error > 0 && (
        <Status variant="chip" tone="bad">
          {summary.error} error{summary.error === 1 ? "" : "s"}
        </Status>
      )}
      {summary.warning > 0 && (
        <Status variant="chip" tone="warn">
          {summary.warning} warning{summary.warning === 1 ? "" : "s"}
        </Status>
      )}
      {summary.error === 0 && summary.warning === 0 && summary.total > 0 && (
        <Status variant="chip" tone="good">
          {summary.total} healthy
        </Status>
      )}
      {fetching && (
        <Status variant="chip" tone="neutral" pulse>
          refreshing
        </Status>
      )}
    </div>
  );
}

const TONE_BY_SEVERITY: Record<HealthSeverity, StatusTone> = {
  ok: "good",
  warning: "warn",
  error: "bad",
};

const LABEL_BY_SEVERITY: Record<HealthSeverity, string> = {
  ok: "Healthy",
  warning: "Warning",
  error: "Error",
};

function CheckCard({ check }: { check: HealthCheck }) {
  const tone = TONE_BY_SEVERITY[check.severity];
  return (
    <article className="rounded-md border border-line bg-surface/30 px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 inline-flex shrink-0">
          <Status variant="dot" tone={tone} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate font-display text-sm font-semibold tracking-display-tight">
              {check.title}
            </h2>
            <Status variant="chip" tone={tone}>
              {LABEL_BY_SEVERITY[check.severity]}
            </Status>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-foreground/70">
            {check.message}
          </p>
        </div>
        {check.link && (
          <Link
            to={check.link}
            className="focus-ring shrink-0 self-center rounded-md border border-line bg-surface-2/40 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-foreground/75 transition hover:border-line-strong hover:text-foreground"
          >
            Fix this →
          </Link>
        )}
      </div>
    </article>
  );
}
